/**
 * Access-token claims → Karexia roles, issuer-aware (shared `core` realm migration).
 *
 * Mirrors the backend (pubsocialnet_common.auth realm policy):
 *  - a token from a LEGACY issuer (the dedicated Karexia realm, listed in
 *    `config.keycloak.legacyIssuers`) keeps today's behaviour: realm roles
 *    (`realm_access.roles`) ∪ the optional flat `roles` claim ∪ every
 *    `resource_access.<client>.roles` (all clients of that realm are Karexia's);
 *  - a token from ANY other issuer (the shared `core` realm, used by unrelated
 *    applications too) counts ONLY `resource_access[roleClientId].roles`. Realm
 *    roles, the flat `roles` claim and other clients' roles are ignored entirely,
 *    so an ADMIN of another application gets no Karexia privilege.
 *
 * Fail closed: with no `legacyIssuers` configured, no issuer is legacy.
 */

import { getConfig, type AppConfig } from '@/config';

export const DEFAULT_ROLE_CLIENT_ID = 'pubsocialnet-backend';

export interface TokenClaims {
  iss?: string;
  sub?: string;
  email?: string;
  tenant_id?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] } | undefined>;
  roles?: string[];
  [key: string]: unknown;
}

/** The subset of the Keycloak config the role policy needs. */
export interface RolePolicy {
  roleClientId: string;
  legacyIssuers: readonly string[];
}

/** Decode a JWT payload (base64url, UTF-8 safe). Null when not a decodable JWT. */
export function decodeJwtPayload(token: string | null | undefined): TokenClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as TokenClaims) : null;
  } catch {
    return null;
  }
}

function normaliseIssuer(iss: string): string {
  return iss.trim().replace(/\/+$/, '');
}

/** Build the role policy from a config's keycloak block (defaults fail closed). */
export function policyFromConfig(cfg: Pick<AppConfig, 'keycloak'> | null | undefined): RolePolicy {
  const kc = cfg?.keycloak;
  return {
    roleClientId: kc?.roleClientId || DEFAULT_ROLE_CLIENT_ID,
    legacyIssuers: Array.isArray(kc?.legacyIssuers) ? kc!.legacyIssuers! : [],
  };
}

/** The active policy from the loaded runtime config (fail-closed defaults if unloaded). */
export function currentRolePolicy(): RolePolicy {
  try {
    return policyFromConfig(getConfig());
  } catch {
    return policyFromConfig(null);
  }
}

/** True when the token comes from an issuer whose realm roles are still honoured. */
export function isLegacyIssuer(claims: TokenClaims | null, policy: RolePolicy): boolean {
  if (!claims || typeof claims.iss !== 'string' || !claims.iss) return false;
  const iss = normaliseIssuer(claims.iss);
  return policy.legacyIssuers.some((l) => typeof l === 'string' && normaliseIssuer(l) === iss);
}

function stringRoles(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((r): r is string => typeof r === 'string') : [];
}

function clientRoles(claims: TokenClaims, clientId: string): string[] {
  const ra = claims.resource_access;
  if (!ra || typeof ra !== 'object') return [];
  const entry = Object.prototype.hasOwnProperty.call(ra, clientId) ? ra[clientId] : undefined;
  return stringRoles(entry?.roles);
}

/** Lowercased, de-duplicated Karexia roles for these claims under `policy`. */
export function resolveKarexiaRoles(claims: TokenClaims | null, policy: RolePolicy): string[] {
  if (!claims) return [];
  let raw: string[];
  if (isLegacyIssuer(claims, policy)) {
    raw = [...stringRoles(claims.realm_access?.roles), ...stringRoles(claims.roles)];
    const ra = claims.resource_access;
    if (ra && typeof ra === 'object') {
      for (const entry of Object.values(ra)) raw.push(...stringRoles(entry?.roles));
    }
  } else {
    raw = clientRoles(claims, policy.roleClientId);
  }
  // Keycloak roles are often UPPERCASE (ADMIN/OWNER/…); lowercase at the source.
  return Array.from(new Set(raw.map((r) => r.toLowerCase())));
}

/**
 * Whether the signed-in account may use Karexia at all. Legacy-realm tokens
 * always may (unchanged behaviour); a shared-realm token needs at least one role
 * of the Karexia role client.
 */
export function hasKarexiaAccess(claims: TokenClaims | null, policy: RolePolicy): boolean {
  if (!claims) return false;
  if (isLegacyIssuer(claims, policy)) return true;
  return clientRoles(claims, policy.roleClientId).length > 0;
}

/** Convenience: roles straight from an access token with the active config. */
export function rolesFromAccessToken(token: string | null | undefined, policy: RolePolicy = currentRolePolicy()): string[] {
  return resolveKarexiaRoles(decodeJwtPayload(token), policy);
}
